import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";

export type ApiUser = {
  isAuthenticated: boolean;
  userId?: string;
  name?: string;
  email?: string;
  roles?: string[];
};

@Injectable({ providedIn: "root" })
export class EasyAuthService {
  private readonly userSubject = new BehaviorSubject<ApiUser | null>(null);
  private readonly authReadySubject = new BehaviorSubject<boolean>(false);

  private readonly settlingKey = "easyAuthSettling";
  private readonly redirectKey = "postLoginRedirect";

  constructor(private readonly http: HttpClient) {}

  async loadUser(): Promise<ApiUser | null> {
    try {
      const user = await firstValueFrom(
        this.http.get<ApiUser>("/api/me", { withCredentials: true }),
      );

      // Auth succeeded — check for a stored redirect
      const pendingRedirect = sessionStorage.getItem(this.redirectKey);
      sessionStorage.removeItem(this.settlingKey);
      sessionStorage.removeItem(this.redirectKey);

      this.userSubject.next(user);
      this.authReadySubject.next(true);

      // Navigate to the originally requested URL if it differs from current
      if (
        pendingRedirect &&
        pendingRedirect !== "/" &&
        pendingRedirect !== window.location.pathname + window.location.search
      ) {
        window.location.replace(pendingRedirect);
      }

      return user;
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        if (sessionStorage.getItem(this.settlingKey)) {
          this.userSubject.next(null);
          this.authReadySubject.next(true);
          return null;
        }

        // Save the current URL before redirecting to EasyAuth
        const currentPath = window.location.pathname + window.location.search;
        if (
          currentPath &&
          currentPath !== "/" &&
          !currentPath.startsWith("/api/")
        ) {
          sessionStorage.setItem(this.redirectKey, currentPath);
        }

        sessionStorage.setItem(this.settlingKey, "true");

        const returnTo =
          currentPath && !currentPath.startsWith("/api/") ? currentPath : "/";

        window.location.replace(
          "/.auth/login/aad?post_login_redirect_uri=" +
            encodeURIComponent(returnTo),
        );

        this.userSubject.next(null);
        return null;
      }

      this.userSubject.next(null);
      this.authReadySubject.next(true);
      return null;
    }
  }

  getUser(): Observable<ApiUser | null> {
    return this.userSubject.asObservable();
  }

  get authReady$(): Observable<boolean> {
    return this.authReadySubject.asObservable();
  }

  isAuthReady(): Observable<boolean> {
    return this.authReadySubject.asObservable();
  }

  isAuthenticated(): boolean {
    return this.userSubject.value?.isAuthenticated === true;
  }

  login(returnTo: string = "/"): void {
    const safeReturnTo = returnTo.startsWith("/api/") ? "/" : returnTo;

    // Save for post-login redirect
    if (safeReturnTo !== "/") {
      sessionStorage.setItem(this.redirectKey, safeReturnTo);
    }

    window.location.replace(
      "/.auth/login/aad?post_login_redirect_uri=" +
        encodeURIComponent(safeReturnTo),
    );
  }

  logout(returnTo: string = "/"): void {
    const safeReturnTo = returnTo.startsWith("/api/") ? "/" : returnTo;

    sessionStorage.removeItem(this.redirectKey);

    window.location.replace(
      "/.auth/logout?post_logout_redirect_uri=" +
        encodeURIComponent(safeReturnTo),
    );
  }
}
